import React from 'react';
import { format } from 'date-fns';
import { RouteComponentProps } from 'react-router';
import { Card, CardBody } from '../../components/Bootstrap';
import { AuthService, AuthTokenResponse } from '../../AuthService';
import { CreateAccountForm } from '../../components/CreateAccountForm';
import { LoginForm } from '../../components/LoginForm';
import { NotificationModal } from '../../components/NotificationModal';
import { ErrorAlert } from '../../components/ErrorAlert';
import { IndicatorDot } from '../../components/IndicatorDot';
import { SaveVenueMutationVariables } from './__generated__/SaveVenueMutation';
import { VenueInviteQuery_venueInvite } from './__generated__/VenueInviteQuery';
import './Onboarding.scss';
import { OnboardingStep } from './types';
import { VenueFormWizard } from './VenueFormWizard';
import { Confirmation } from './Confirmation';

interface OnboardingProps extends RouteComponentProps {
  loading: boolean;
  invite?: VenueInviteQuery_venueInvite;
  uuid: string;
  steps?: OnboardingStep[];
  auth: AuthService;
  venueId?: string;
  error: Error | null;
  login(token: string): Promise<void>;
  onSubmit({ variables }: { variables: SaveVenueMutationVariables }): void;
  onSaveForLater(data);
  clearError();
}

interface OnboardingState {
  savedForLater: boolean;
  data: any;
  step: number;
  credentials?: {
    email: string;
    password: string;
  };
  completed: boolean;
  missingDob: boolean;
}

// const TEST_DATA = {
//   listing: {
//     name: 'Drews Venue',
//     address: '695 Atlantic Ave',
//     city: 'Boston',
//     state: 'MA',
//     zip: '02120',
//     phone: '207 608 0136',
//     imageUrl: 'img',
//   },
//   business: {
//     name: 'Drews Venue',
//     address: '695 Atlantic Ave',
//     city: 'Boston',
//     state: 'MA',
//     zip: '02120',
//     phone: '207 608 0136',
//   },
//   contact: {
//     pointOfContactFirstName: 'Drew',
//     pointOfContactLastName: 'Pomerleau',
//     email: 'drew+test@getnoble.co',
//     phone: '207 608 0136',
//     dob: new Date(1995, 10, 11).toISOString(),
//   },
//   // payment: {
//   //   name: 'Drew Pomerleau',
//   //   address: '695 Atlantic Ave',
//   //   city: 'Boston',
//   //   state: 'Massachusetts',
//   //   zip: '02120',
//   //   phone: '207 608 0136',
//   // },
// };

export class Onboarding extends React.Component<OnboardingProps, OnboardingState> {
  public notificationTimer;

  constructor(props: OnboardingProps) {
    super(props);

    this.state = {
      data: {},
      step: 0,
      savedForLater: false,
      completed: false,
      missingDob: false,
    };
  }

  public componentDidUpdate() {
    if (this.props.invite && this.props.invite.savedData && Object.keys(this.state.data).length === 0) {
      const { savedData } = this.props.invite;
      if (savedData && savedData.contact && savedData.contact.dob) {
        savedData.contact.dob = new Date(savedData.contact.dob);
      }

      if (savedData.listing && savedData.listing.image && savedData.listing.image.url) {
        savedData.listing.image = {
          url: savedData.listing.image.url,
        };
      }

      this.setState({ data: savedData || {} });
    }
  }

  public componentWillUnmount() {
    clearTimeout(this.notificationTimer);
  }

  public login = async ({ email, password }, authToken: AuthTokenResponse) => {
    await this.props.login(authToken.accessToken);
    this.setState({
      credentials: {
        email,
        password,
      },
    });
  };

  public register = async ({ email, password }, authToken: AuthTokenResponse) => {
    await this.login({ email, password }, authToken);
    const variables = {
      uuid: this.props.uuid,
      data: {},
    };

    await this.props.onSaveForLater({ variables });
  };

  public onSubmitStep = () => {
    const onLastStep = this.state.step === this.props.steps.length - 1;
    this.setState(
      {
        step: onLastStep ? this.state.step : this.state.step + 1,
        missingDob: false,
      },
      async () => {
        if (onLastStep) {
          this.props.clearError();
          const { data } = this.state;

          try {
            await this.props.onSubmit({
              variables: {
                uuid: this.props.uuid,
                image: data.listing.image.url ? undefined : data.listing.image,
                data: {
                  name: data.listing.name,
                  pointOfContactEmail: data.contact.email,
                  address: {
                    id: undefined as any,
                    street1: data.listing.address,
                    city: data.listing.city,
                    state: data.listing.state,
                    zip: data.listing.zip,
                  },
                  businessAddress: {
                    id: undefined as any,
                    name: data.business.name,
                    street1: data.business.address,
                    city: data.business.city,
                    state: data.business.state,
                    zip: data.business.zip,
                    phone: data.business.phone,
                  },
                  ownerAddress: {
                    id: undefined as any,
                    name: `${data.contact.pointOfContactFirstName} ${data.contact.pointOfContactLastName}`,
                    street1: data.business.address,
                    city: data.business.city,
                    state: data.business.state,
                    zip: data.business.zip,
                    phone: data.contact.phone,
                  },
                  paymentAddress:
                    data.payment && data.payment.name
                      ? {
                          id: undefined as any,
                          name: data.payment.name,
                          street1: data.payment.address,
                          city: data.payment.city,
                          state: data.payment.state,
                          zip: data.payment.zip,
                          phone: data.payment.phone,
                        }
                      : undefined,
                  pointOfContactFirstName: data.contact.pointOfContactFirstName,
                  pointOfContactLastName: data.contact.pointOfContactLastName,
                  businessRegistrationNumber: data.business.businessId,
                  liquorLicense: data.business.liquorLicense,
                  itin: data.business.itin,
                  paymentProcessor: this.props.invite!.paymentProcessor,
                  ownerDOB: data.contact.dob ? format(new Date(data.contact.dob), 'yyyy-MM-dd') : null,
                  paymentInfo:
                    data.payment && data.payment.name && data.payment.routing
                      ? {
                          routingNumber: data.payment.routing,
                          accountNumber: data.payment.account,
                          taxId: data.payment.taxId,
                        }
                      : undefined,
                },
              },
            });

            if (this.state.credentials) {
              const tokenResponse = await this.props.auth.login(
                this.state.credentials.email,
                this.state.credentials.password
              );
              await this.props.login(tokenResponse.accessToken);
            }

            this.setState({ completed: true });
          } catch {}
        }
      }
    );
  };

  public saveForLater = async () => {
    const data = {
      ...this.state.data,
      listing: {
        ...this.state.data.listing,
        image: undefined,
      },
    };

    if (data.contact) {
      data.contact = {
        ...data.contact,
        dob: undefined,
      };
    }

    const variables = {
      uuid: this.props.uuid,
      data,
    };

    await this.props.onSaveForLater({ variables });

    this.setState({ savedForLater: true });
    this.notificationTimer = setTimeout(this.hideSavedForLater, 3000);
  };

  public hideSavedForLater = () => {
    clearTimeout(this.notificationTimer);
    this.setState({ savedForLater: false });
  };

  public onChange = (step, data) => {
    this.setState({
      data: Object.assign({}, this.state.data, {
        [step]: data,
      }),
    });
  };

  public onStepBack = () => {
    this.setState({
      step: this.state.step - 1,
    });
  };

  public goToStep = (step: number) => {
    if (step < this.state.step) {
      this.setState({ step });
    }
  };

  renderContent() {
    if (this.props.loading || !this.props.invite) {
      return 'Loading...';
    } else if (!this.props.auth.isLoggedIn) {
      if (this.props.invite.userId) {
        return <LoginForm auth={this.props.auth} onLogin={this.login} email={this.props.invite.email} />;
      } else {
        return <CreateAccountForm auth={this.props.auth} onLogin={this.register} invite={this.props.invite} />;
      }
    } else if (this.state.completed) {
      return (
        <Confirmation
          venueId={this.props.venueId!}
          isLoggedIn={!!this.state.credentials}
          venueName={this.state.data.listing.name}
        />
      );
    } else {
      const step = this.props.steps[this.state.step];

      return (
        <CardBody>
          {this.state.missingDob && (
            <div className='text-danger'>
              Please make sure you have entered your date of birth on the previous step.
            </div>
          )}
          <VenueFormWizard
            step={step}
            data={this.state.data[step.key]}
            isOnFirstStep={this.state.step === 0}
            isOnLastStep={this.state.step === this.props.steps.length - 1}
            onSubmitStep={this.onSubmitStep}
            onStepBack={this.onStepBack}
            onSaveForLater={this.saveForLater}
            onChange={this.onChange}
          />
          <div className='d-flex justify-content-center mt-4'>
            {this.props.steps.map((_, i) => (
              <IndicatorDot data={i} onClick={this.goToStep} key={i} active={i === this.state.step} />
            ))}
          </div>
        </CardBody>
      );
    }
  }

  render() {
    return (
      <div className='onboarding-block-container'>
        <div className='onboarding-block'>
          <ErrorAlert onClear={this.props.clearError} error={this.props.error} />
          <Card className='p-4'>{this.renderContent()}</Card>
        </div>
        <NotificationModal hidden={!this.state.savedForLater} text='Saved!' onClick={this.hideSavedForLater} />
      </div>
    );
  }
}
